import { useState } from "react";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { publicApi, formatApiErrorDetail } from "../../lib/api";
import { trackLead } from "../../lib/metaPixel";

const REQUIREMENTS = [
  "Brand Creation / Rebranding", "Multimedia Production", "Social Media", "Google / Meta Advertising",
  "SEO / Google Business Profile", "Lead Generation", "Content Creation", "AI Marketing",
  "Complete Marketing Support", "Not Sure — I need advice",
];
const BUDGETS = ["Under ₹25K", "₹25K–₹50K", "₹50K–₹1L", "₹1L–₹3L", "₹3L+", "Not decided"];

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 font-body text-sm text-navy placeholder:text-navy/40 outline-none transition focus:border-[#FF5722] focus:ring-2 focus:ring-[#FF5722]/20";
const labelCls = "mb-1.5 block font-body text-xs font-semibold uppercase tracking-wider text-navy/60";

export default function LeadForm({ compact = false, source = "contact" }) {
  const [form, setForm] = useState({
    name: "", company: "", mobile: "", email: "", website: "", city: "",
    industry: "", requirement: "", budget: "", objective: "", best_time: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [serverErr, setServerErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Name is required";
    if (!form.mobile.trim()) er.mobile = "Mobile / WhatsApp is required";
    else if (!/^[+0-9\s-]{7,15}$/.test(form.mobile.trim())) er.mobile = "Enter a valid number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerErr("");
    if (!validate()) return;
    setStatus("loading");
    try {
      if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
        // Safe local preview: test submission feedback without mutating live production database
        await new Promise((resolve) => setTimeout(resolve, 600));
        setStatus("success");
        try {
          trackLead({ source, requirement: form.requirement, budget: form.budget });
        } catch (pixelErr) {
          console.warn("[MetaPixel] Non-blocking lead tracking error:", pixelErr);
        }
        return;
      }
      await publicApi.post("/leads", { ...form, source });
      setStatus("success");
      try {
        trackLead({ source, requirement: form.requirement, budget: form.budget });
      } catch (pixelErr) {
        console.warn("[MetaPixel] Non-blocking lead tracking error:", pixelErr);
      }
    } catch (err) {
      setStatus("error");
      setServerErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (status === "success") {
    return (
      <div data-testid="lead-form-success" className="flex flex-col items-center rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircle2 className="mb-3 text-emerald-500" size={44} />
        <h4 className="font-display text-2xl font-bold uppercase text-navy">Thank You</h4>
        <p className="mt-2 max-w-sm font-body text-sm text-navy/60">
          Your request has reached ADMALL. We'll review your goals and get back to you shortly.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Name *" err={errors.name}>
        <input data-testid="lead-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
      </Field>
      <Field label="Company / Brand">
        <input data-testid="lead-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" />
      </Field>
      <Field label="Mobile / WhatsApp *" err={errors.mobile}>
        <input data-testid="lead-mobile" className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="+91 ..." />
      </Field>
      <Field label="Email" err={errors.email}>
        <input data-testid="lead-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
      </Field>

      {!compact && (
        <>
          <Field label="Website / Social Media">
            <input data-testid="lead-website" className={inputCls} value={form.website} onChange={set("website")} placeholder="Link" />
          </Field>
          <Field label="City / Location">
            <input data-testid="lead-city" className={inputCls} value={form.city} onChange={set("city")} placeholder="City" />
          </Field>
          <Field label="Business / Industry">
            <input data-testid="lead-industry" className={inputCls} value={form.industry} onChange={set("industry")} placeholder="Industry" />
          </Field>
          <Field label="What do you need help with?">
            <select data-testid="lead-requirement" className={inputCls} value={form.requirement} onChange={set("requirement")}>
              <option value="">Select an option</option>
              {REQUIREMENTS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Approx. monthly marketing budget">
            <select data-testid="lead-budget" className={inputCls} value={form.budget} onChange={set("budget")}>
              <option value="">Select budget</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Best time to contact you">
            <input data-testid="lead-besttime" className={inputCls} value={form.best_time} onChange={set("best_time")} placeholder="e.g. Weekday mornings" />
          </Field>
          <div className="sm:col-span-2">
            <label className={labelCls}>Primary objective</label>
            <textarea data-testid="lead-objective" rows={3} className={inputCls} value={form.objective} onChange={set("objective")} placeholder="What are you trying to achieve?" />
          </div>
        </>
      )}

      {compact && (
        <div className="sm:col-span-2">
          <label className={labelCls}>What are you trying to achieve?</label>
          <textarea data-testid="lead-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us your goal" />
        </div>
      )}

      {status === "error" && (
        <p data-testid="lead-form-error" className="sm:col-span-2 rounded-lg bg-red-50 px-4 py-2 font-body text-sm text-red-600">{serverErr}</p>
      )}

      <div className="sm:col-span-2">
        <button
          data-testid="lead-submit"
          type="submit"
          disabled={status === "loading"}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF5722] px-8 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition hover:bg-[#E64A19] disabled:opacity-60 sm:w-auto"
        >
          {status === "loading" ? <Loader2 className="animate-spin" size={18} /> : <>{compact ? "Get My Free Growth Check" : "Request a Free Growth Consultation"} <ArrowRight size={17} /></>}
        </button>
      </div>
    </form>
  );
}

function Field({ label, err, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {err && <p className="mt-1 font-body text-xs text-red-500">{err}</p>}
    </div>
  );
}
